"use client";
import { useEffect, useState } from "react";
import Script from "next/script";

type CookieYesConsentDetail = {
  accepted: string[];
  rejected: string[];
};

export function useCookieYesConsent() {
  const [detail, setDetail] = useState<CookieYesConsentDetail | null>(null);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  // Wait for hydration to complete before any DOM operations
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<CookieYesConsentDetail>;
      setDetail(customEvent.detail);
    };

    document.addEventListener("cookieyes_consent_update", handler);

    return () =>
      document.removeEventListener("cookieyes_consent_update", handler);
  }, []);

  useEffect(() => {
    // Skip all DOM operations until hydration is complete
    if (!isHydrated) return;

    const isAnalyticsAccepted = detail?.accepted.includes("analytics") ?? false;

    setAnalyticsAllowed((prevAllowed) => {
      if (!isAnalyticsAccepted) {
        // Clean up Clarity global and cookies without touching the DOM tree.
        // Direct DOM removal (removeChild) conflicts with React's virtual DOM
        // and causes "insertBefore" NotFoundError during hydration.
        if (typeof window !== "undefined" && window.clarity) {
          delete window.clarity;
        }
        document.cookie = `_clck=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `_clsk=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        console.log("⚡ Clarity fully unloaded");
      } else if (!prevAllowed) {
        // csak akkor remount, ha eddig NEM volt engedélyezve
        setRefresh((k) => k + 1);
        console.log("⚡ Clarity loaded");
      }
      return isAnalyticsAccepted;
    });
  }, [detail, isHydrated]);

  return analyticsAllowed ? (
    <Script
      key={refresh}
      id="ms-clarity"
      strategy="afterInteractive"
      data-cookieyes="cookieyes-analytics"
      dangerouslySetInnerHTML={{
        __html: `(function(c,l,a,r,i,t,y){
  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i+"?v="+Date.now();
  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "sjuxkr79er");`,
      }}
    />
  ) : null;
}